/**
 * Validation utilities for third-party tracking pixel IDs and cookies.
 * These validators prevent malformed values from propagating through the tracking pipeline.
 */

/**
 * Facebook Pixel IDs are 15-16 digit numbers.
 * This validation prevents XSS attacks when the pixel ID is interpolated into script content.
 *
 * @param pixelId - The Facebook Pixel ID to validate
 * @returns true if the pixel ID matches the expected format (15-16 digits)
 */
export const isValidFacebookPixelId = (pixelId: string): boolean => {
  return /^\d{15,16}$/.test(pixelId);
};

/**
 * Validates _fbc cookie format: fb.{version}.{timestamp}.{fbclid}
 * Rejects malformed values like literal Everflow macro placeholders (e.g. `{fbclid}`).
 */
export const FBC_COOKIE_PATTERN = /^fb\.\d\.\d{13}\.[a-zA-Z0-9_-]{10,}$/;
export const isValidFbcCookie = (fbc: string): boolean => FBC_COOKIE_PATTERN.test(fbc);

/**
 * Validates _fbp cookie format: fb.{version}.{timestamp}.{random_number}
 */
export const FBP_COOKIE_PATTERN = /^fb\.\d\.\d{13}\.\d{8,}$/;
export const isValidFbpCookie = (fbp: string): boolean => FBP_COOKIE_PATTERN.test(fbp);

/**
 * Formats a raw `fbclid` URL parameter into the Meta `_fbc` cookie format:
 * `fb.1.{timestampMs}.{fbclid}`. Used when we capture `fbclid` directly from
 * the URL but never receive a real `_fbc` cookie from the browser, so that
 * downstream consumers (e.g. Everflow `adv5`, Meta CAPI) receive a value in
 * the canonical format. The timestamp should be as close to the click time
 * as possible — typically the moment the session token is minted.
 */
export const formatFbc = (fbclid: string, timestampMs: number): string => {
  return `fb.1.${timestampMs}.${fbclid}`;
};

/** Meta's click-through attribution window. */
export const FBC_ATTRIBUTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Reads the click timestamp Meta embeds in `fb.{version}.{timestampMs}.{fbclid}`. */
export const getFbcClickTimestampMs = (fbc: string): number | null => {
  if (!isValidFbcCookie(fbc)) return null;
  const timestampMs = Number(fbc.split(".")[2]);
  return Number.isFinite(timestampMs) ? timestampMs : null;
};

/**
 * True when the click an `_fbc` describes is still inside Meta's window. Guards
 * the session-derived fbc, which is stamped once at session creation and never
 * refreshed — a returning visitor's token still holds their first click. Real
 * `_fbc` cookies need no guard; the Pixel rewrites them on every click.
 */
export const isFbcWithinAttributionWindow = (fbc: string, nowMs: number): boolean => {
  const clickMs = getFbcClickTimestampMs(fbc);
  if (clickMs === null) return false;
  return clickMs > nowMs - FBC_ATTRIBUTION_WINDOW_MS;
};
